import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import { toast } from 'react-hot-toast';
import {
  CalendarIcon,
  UserIcon,
  TruckIcon,
  FunnelIcon,
  ArrowPathIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import InvoiceActionMenu from '../../components/invoices/InvoiceActionMenu';
import ReservationDetailDrawer from '../../components/reservations/ReservationDetailDrawer';
import ReservationEditorModal from '../../components/reservations/ReservationEditorModal';
import EntityDrawer from '../../components/ui/EntityDrawer';

type ReservationRecord = {
  id: string;
  reservationRef?: string;
  sourceType?: string;
  splitBillingConfig?: Record<string, any> | null;
  customerReference?: string | null;
  createdAt?: string;
  startDate?: string;
  endDate?: string;
  totalPrice?: string | number;
  depositAmount?: string | number;
  status: string;
  assignmentStatus?: string;
  driverAssignmentStatus?: string;
  driverReadiness?: {
    required?: number;
    assigned?: number;
    blockingIssues?: string[];
    warnings?: string[];
  };
  paymentStatus?: string;
  lifecycleLabel?: string;
  vehicleRequirements?: any[];
  vehicleAssignments?: any[];
  driverAssignments?: any[];
  quoteSource?: {
    number?: string | null;
    depositPaymentUrl?: string | null;
    depositInvoiceNumber?: string | null;
  } | null;
  vehicleId?: string;
  vehicle?: {
    make?: string;
    model?: string;
    licensePlate?: string;
  } | null;
  tripDetails?: {
    customer_name?: string | null;
    customer_email?: string | null;
    pickup_location?: string | null;
    dropoff_location?: string | null;
    vehicle_label?: string | null;
    vehicle_type?: string | null;
    customer_company_name?: string | null;
    quote_id?: string | null;
    quote_number?: string | null;
  } | null;
  customer?: {
    id?: string | null;
    email?: string | null;
    name?: string | null;
  } | null;
  account?: {
    id?: string | null;
    name?: string | null;
  } | null;
  user?: {
    id?: string;
    email?: string;
    kycStatus?: string;
  } | null;
};

type StatusActionState = {
  mode: 'single' | 'bulk';
  reservationIds: string[];
  operation: string;
  label: string;
};

function formatDollars(value?: string | number | null) {
  const amount = Number(value || 0);
  const normalized = Number.isFinite(amount) ? amount : 0;
  return `$${normalized.toFixed(2)}`;
}

function formatShortDateTime(value?: string | null) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatLabel(value?: string | null) {
  const normalized = (value || '').trim();
  if (!normalized) {
    return 'N/A';
  }

  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function getLifecycleColor(label?: string | null) {
  const normalized = String(label || '').toLowerCase();
  if (normalized.includes('pending')) return 'bg-amber-100 text-amber-800';
  if (normalized.includes('partial')) return 'bg-sky-100 text-sky-800';
  if (normalized.includes('assigned') || normalized.includes('ready')) return 'bg-green-100 text-green-800';
  if (normalized.includes('progress')) return 'bg-purple-100 text-purple-800';
  if (normalized.includes('cancel')) return 'bg-red-100 text-red-800';
  if (normalized.includes('complete')) return 'bg-blue-100 text-blue-800';
  return 'bg-surface-container-high text-on-surface';
}

function getAssignmentLabel(reservation: ReservationRecord) {
  const assignments = Array.isArray(reservation.vehicleAssignments) ? reservation.vehicleAssignments : [];
  const requirements = Array.isArray(reservation.vehicleRequirements) ? reservation.vehicleRequirements : [];
  const required = requirements.reduce((sum, requirement) => sum + Number(requirement?.quantity || 0), 0);
  if (requirements.length) {
    return `${assignments.length}/${required || requirements.length} assigned`;
  }
  return assignments.length ? `${assignments.length} assigned` : formatLabel(reservation.assignmentStatus || 'unassigned');
}

function getDriverAssignmentLabel(reservation: ReservationRecord) {
  const assignments = Array.isArray(reservation.driverAssignments) ? reservation.driverAssignments : [];
  if (assignments.length) {
    const names = assignments
      .map((assignment: any) => assignment.driver?.name)
      .filter(Boolean);
    if (names.length) {
      return names.join(', ');
    }
  }
  return formatLabel(reservation.driverAssignmentStatus || 'unassigned');
}

function getPaymentLabel(reservation: ReservationRecord) {
  const paymentStatus = String(reservation.paymentStatus || '').toLowerCase();
  if (paymentStatus === 'deposit_paid') return 'Deposit paid';
  if (paymentStatus === 'deposit_due') return 'Deposit due';
  return 'No deposit';
}

function getReservationLabel(reservation: ReservationRecord) {
  return reservation.reservationRef || `Reservation ${reservation.id.slice(0, 8)}`;
}

function getVehicleLabel(reservation: ReservationRecord) {
  if (reservation.vehicle?.make || reservation.vehicle?.model) {
    return [reservation.vehicle?.make, reservation.vehicle?.model].filter(Boolean).join(' ');
  }

  return reservation.tripDetails?.vehicle_label || reservation.tripDetails?.vehicle_type || 'Imported reservation';
}

function getCustomerLabel(reservation: ReservationRecord) {
  return reservation.customer?.name || reservation.tripDetails?.customer_name || reservation.tripDetails?.customer_email || reservation.customer?.email || reservation.user?.email || 'Unknown customer';
}

export default function AdminReservations() {
  const [filters, setFilters] = useState({
    status: '',
    search: '',
	    sourceType: '',
	    assignmentStatus: '',
	    driverAssignmentStatus: '',
	    paymentStatus: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 20
  });
  const [selectedReservations, setSelectedReservations] = useState<string[]>([]);
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('reservation'));
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editorReservationId, setEditorReservationId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<StatusActionState | null>(null);
  const [statusReason, setStatusReason] = useState('');
  
  const queryClient = useQueryClient();

  const { data: reservationsData, isLoading, isFetching, error } = useQuery(
    ['admin-reservations', filters],
    async () => {
      const response = await adminAPI.getAllReservations({
        status: filters.status || undefined,
        search: filters.search || undefined,
	        sourceType: filters.sourceType || undefined,
	        assignmentStatus: filters.assignmentStatus || undefined,
	        driverAssignmentStatus: filters.driverAssignmentStatus || undefined,
	        paymentStatus: filters.paymentStatus || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        page: filters.page,
        limit: filters.limit,
      });
      return response.data;
    },
    { 
      keepPreviousData: true
    }
  );

  const reservations = useMemo<ReservationRecord[]>(() => {
    return Array.isArray(reservationsData?.data) ? reservationsData.data : [];
  }, [reservationsData?.data]);
  const pagination = reservationsData?.pagination;
  const summary = reservationsData?.summary || {};

  const importedReservations = useMemo(
    () => reservations.filter((reservation) => String(reservation.sourceType || '').toLowerCase() === 'import').length,
    [reservations],
  );
  const pendingDepositReservations = useMemo(
    () => reservations.filter((reservation) => reservation.paymentStatus === 'deposit_due').length,
    [reservations],
  );
	  const unassignedReservations = useMemo(
	    () => reservations.filter((reservation) => ['unassigned', 'partial'].includes(String(reservation.assignmentStatus || ''))).length,
	    [reservations],
	  );
	  const needsDriverReservations = useMemo(
	    () => reservations.filter((reservation) => ['unassigned', 'partial'].includes(String(reservation.driverAssignmentStatus || ''))).length,
	    [reservations],
	  );
  const todayReservations = useMemo(
    () => reservations.filter((reservation) => {
      if (reservation.status === 'active') return true;
      if (!reservation.startDate) return false;
      const start = new Date(reservation.startDate);
      const now = new Date();
      return !Number.isNaN(start.getTime()) && start.toDateString() === now.toDateString();
    }).length,
    [reservations],
  );

  useEffect(() => {
    const handleDocumentClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reservationId = params.get('reservationId');
    if (reservationId) {
      setSelectedReservationId(reservationId);
    }
  }, []);

  useEffect(() => {
    setSelectedReservations((current) => current.filter((id) => reservations.some((reservation) => reservation.id === id)));
  }, [reservations]);

  const updateStatusMutation = useMutation(
    ({ id, data }: { id: string; data: { status: string; reason?: string } }) =>
      adminAPI.updateReservationStatus(id, data),
    {
      onSuccess: () => {
        toast.success('Reservation status updated');
        queryClient.invalidateQueries('admin-reservations');
        setStatusAction(null);
        setStatusReason('');
        if (selectedReservationId) {
          queryClient.invalidateQueries(['admin-reservation', selectedReservationId]);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update status');
      }
    }
  );

  const bulkOperationMutation = useMutation(
    adminAPI.bulkReservationOperation,
    {
      onSuccess: (response) => {
        const { success, failed } = response.data;
        toast.success(`${success.length} reservations updated successfully`);
        if (failed.length > 0) {
          toast.error(`${failed.length} reservations failed to update`);
        }
        queryClient.invalidateQueries('admin-reservations');
        setSelectedReservations([]);
        setStatusAction(null);
        setStatusReason('');
        if (selectedReservationId) {
          queryClient.invalidateQueries(['admin-reservation', selectedReservationId]);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Bulk operation failed');
      }
    }
  );

  const deleteReservationMutation = useMutation(
    async (reservationId: string) => adminAPI.deleteReservation(reservationId),
    {
      onSuccess: async (_response, reservationId) => {
        toast.success('Reservation deleted');
        await queryClient.invalidateQueries('admin-reservations');
        await queryClient.invalidateQueries(['admin-reservation', reservationId]);
        if (selectedReservationId === reservationId) {
          setSelectedReservationId(null);
        }
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to delete reservation');
      },
    },
  );

  const handleStatusUpdate = (reservationId: string, status: string) => {
    setOpenMenuId(null);
    setStatusReason('');
    setStatusAction({
      mode: 'single',
      reservationIds: [reservationId],
      operation: status,
      label: formatLabel(status),
    });
  };

  const handleBulkOperation = (operation: string) => {
    if (selectedReservations.length === 0) {
      toast.error('Please select reservations first');
      return;
    }

    setStatusReason('');
    setStatusAction({
      mode: 'bulk',
      reservationIds: selectedReservations,
      operation,
      label: formatLabel(operation),
    });
  };

  const submitStatusAction = () => {
    if (!statusAction) {
      return;
    }
    const reason = statusReason.trim() || undefined;
    if (statusAction.mode === 'bulk') {
      bulkOperationMutation.mutate({
        operation: statusAction.operation,
        reservationIds: statusAction.reservationIds,
        reason
      });
      return;
    }
    updateStatusMutation.mutate({
      id: statusAction.reservationIds[0],
      data: { status: statusAction.operation, reason },
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedReservations(reservations.map((reservation) => reservation.id));
    } else {
      setSelectedReservations([]);
    }
  };

  const handleSelectReservation = (reservationId: string, checked: boolean) => {
    if (checked) {
      setSelectedReservations([...selectedReservations, reservationId]);
    } else {
      setSelectedReservations(selectedReservations.filter(id => id !== reservationId));
    }
  };

  const handleCreateReservation = () => {
    setEditorReservationId(null);
    setEditorOpen(true);
  };

  const handleEditReservation = (reservationId: string) => {
    setEditorReservationId(reservationId);
    setEditorOpen(true);
  };

  const handleDeleteReservation = (reservation: ReservationRecord) => {
    const confirmed = window.confirm(`Delete ${getReservationLabel(reservation)}? Any attached invoices will be detached.`);
    if (!confirmed) {
      return;
    }

    deleteReservationMutation.mutate(reservation.id);
  };

  const getReservationActions = (reservation: ReservationRecord) => {
    const actions: Array<{ label: string; onClick: () => void; dangerous?: boolean }> = [
      {
        label: 'View details',
        onClick: () => setSelectedReservationId(reservation.id),
      },
      {
        label: 'Edit reservation',
        onClick: () => handleEditReservation(reservation.id),
      },
    ];

    if (reservation.status === 'pending') {
      actions.push({
        label: 'Mark confirmed',
        onClick: () => handleStatusUpdate(reservation.id, 'confirmed'),
      });
      actions.push({
        label: 'Cancel reservation',
        dangerous: true,
        onClick: () => handleStatusUpdate(reservation.id, 'cancelled'),
      });
    }

    if (reservation.status === 'confirmed' || reservation.status === 'active') {
      actions.push({
        label: 'Mark completed',
        onClick: () => handleStatusUpdate(reservation.id, 'completed'),
      });
    }

    if (reservation.status === 'confirmed') {
      actions.push({
        label: 'Cancel reservation',
        dangerous: true,
        onClick: () => handleStatusUpdate(reservation.id, 'cancelled'),
      });
    }

    actions.push({
      label: 'Delete reservation',
      dangerous: true,
      onClick: () => handleDeleteReservation(reservation),
    });

    return actions;
  };

  return (
    <PageLayout className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Reservations</h1>
          <p className="mt-1 text-on-surface-variant">
            Confirm service, monitor deposits, and assign exact vehicles for upcoming trips.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching ? (
            <span className="rounded-pill bg-surface-container-high px-3 py-1 text-xs font-medium text-on-surface-variant">
              Refreshing...
            </span>
          ) : null}
          <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={handleCreateReservation}>
            <PlusIcon className="h-4 w-4" />
            New Reservation
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-card border border-error/40 bg-error-container/20 px-4 py-3 text-sm text-on-surface">
          Failed to load reservations. {(error as any)?.response?.data?.message || (error as any)?.message || 'Unknown error'}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <div className="admin-subpanel rounded-card p-4">
          <p className="text-sm text-on-surface-variant">Visible Reservations</p>
          <p className="mt-1 text-2xl font-semibold text-on-surface">{summary.total ?? pagination?.total ?? reservations.length}</p>
        </div>
        <div className="admin-subpanel rounded-card p-4">
          <p className="text-sm text-on-surface-variant">Pending Deposit</p>
          <p className="mt-1 text-2xl font-semibold text-on-surface">{summary.pendingDeposit ?? pendingDepositReservations}</p>
        </div>
		        <div className="admin-subpanel rounded-card p-4">
		          <p className="text-sm text-on-surface-variant">Needs Vehicle</p>
		          <p className="mt-1 text-2xl font-semibold text-on-surface">{summary.needsVehicle ?? unassignedReservations}</p>
		        </div>
		        <div className="admin-subpanel rounded-card p-4">
		          <p className="text-sm text-on-surface-variant">Needs Driver</p>
		          <p className="mt-1 text-2xl font-semibold text-on-surface">{summary.needsDriver ?? needsDriverReservations}</p>
		        </div>
		        <div className="admin-subpanel rounded-card p-4">
          <p className="text-sm text-on-surface-variant">Today / Active</p>
          <p className="mt-1 text-2xl font-semibold text-on-surface">{summary.todayOrActive ?? todayReservations}</p>
          {(summary.imported ?? importedReservations) ? (
            <p className="mt-1 text-xs text-on-surface-variant">{summary.imported ?? importedReservations} imported in view</p>
          ) : null}
        </div>
      </div>

      <div className="admin-subpanel rounded-card p-4">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-on-surface">Filters</h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 xl:grid-cols-8">
          <div className="md:col-span-2 xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="search"
              className="input-field"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value, page: 1 })}
              placeholder="Customer, quote, route..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="input-field"
              value={filters.status}
              onChange={(event) => setFilters({ ...filters, status: event.target.value, page: 1 })}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

	          <div>
	            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
            <select
              className="input-field"
              value={filters.assignmentStatus}
              onChange={(event) => setFilters({ ...filters, assignmentStatus: event.target.value, page: 1 })}
            >
              <option value="">All</option>
              <option value="unassigned">Unassigned</option>
              <option value="partial">Partial</option>
              <option value="assigned">Assigned</option>
	            </select>
	          </div>

	          <div>
	            <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
	            <select
	              className="input-field"
	              value={filters.driverAssignmentStatus}
	              onChange={(event) => setFilters({ ...filters, driverAssignmentStatus: event.target.value, page: 1 })}
	            >
	              <option value="">All</option>
	              <option value="unassigned">Unassigned</option>
	              <option value="partial">Partial</option>
	              <option value="assigned">Assigned</option>
	            </select>
	          </div>

	          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deposit</label>
            <select
              className="input-field"
              value={filters.paymentStatus}
              onChange={(event) => setFilters({ ...filters, paymentStatus: event.target.value, page: 1 })}
            >
              <option value="">All</option>
              <option value="deposit_due">Deposit due</option>
              <option value="deposit_paid">Deposit paid</option>
              <option value="not_required">No deposit</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              className="input-field"
              value={filters.sourceType}
              onChange={(event) => setFilters({ ...filters, sourceType: event.target.value, page: 1 })}
            >
              <option value="">All</option>
              <option value="quote">Quote</option>
              <option value="manual">Manual</option>
              <option value="import">Import</option>
              <option value="booking">Booking</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              className="input-field"
              value={filters.startDate}
              onChange={(event) => setFilters({ ...filters, startDate: event.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              className="input-field"
              value={filters.endDate}
              onChange={(event) => setFilters({ ...filters, endDate: event.target.value, page: 1 })}
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
	              onClick={() => setFilters({ status: '', search: '', sourceType: '', assignmentStatus: '', driverAssignmentStatus: '', paymentStatus: '', startDate: '', endDate: '', page: 1, limit: 20 })}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Clear Filters
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-on-surface-variant">
            Reservation relationships and imported trip snapshots are available from the detail drawer.
          </p>
          {selectedReservations.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">{selectedReservations.length} selected</span>
              <button
                onClick={() => handleBulkOperation('confirm')}
                className="rounded-md bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700"
                disabled={bulkOperationMutation.isLoading}
              >
                Confirm
              </button>
              <button
                onClick={() => handleBulkOperation('cancel')}
                className="rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700"
                disabled={bulkOperationMutation.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => handleBulkOperation('complete')}
                className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
                disabled={bulkOperationMutation.isLoading}
              >
                Complete
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <section className="admin-subpanel rounded-card p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-on-surface">Reservations</h2>
          <p className="text-sm text-on-surface-variant">
            Click any row to inspect customer, account, invoice, and payment relationships.
          </p>
        </div>

        {isLoading ? (
          <LoadingSpinner className="py-20" />
        ) : reservations.length > 0 ? (
          <div className="overflow-x-auto rounded-card border border-outline/60">
            <table className="min-w-full divide-y divide-outline">
              <thead className="bg-surface-container-low">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedReservations.length === reservations.length && reservations.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reservation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trip
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    When
                  </th>
	                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
	                    Vehicles
	                  </th>
	                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
	                    Drivers
	                  </th>
	                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
	                    Deposit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lifecycle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-container-lowest divide-y divide-outline">
                {reservations.map((reservation) => (
                  <tr
                    key={reservation.id}
                    className="cursor-pointer hover:bg-surface-container-low"
                    onClick={() => {
                      setSelectedReservationId(reservation.id);
                      setOpenMenuId(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedReservationId(reservation.id);
                        setOpenMenuId(null);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedReservations.includes(reservation.id)}
                        onChange={(e) => handleSelectReservation(reservation.id, e.target.checked)}
                        onClick={(event) => event.stopPropagation()}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-on-surface">
                            {getReservationLabel(reservation)}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span>{formatShortDateTime(reservation.createdAt)}</span>
                            <span className="rounded-pill bg-surface-container-high px-2 py-0.5 text-[11px] font-medium text-on-surface-variant">
                              {formatLabel(reservation.sourceType || 'booking')}
                            </span>
                            {reservation.tripDetails?.quote_number ? (
                              <span className="rounded-pill bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                                {reservation.tripDetails.quote_number}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-on-surface">
                            {getCustomerLabel(reservation)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reservation.account?.name || reservation.tripDetails?.customer_company_name || reservation.customer?.email || reservation.tripDetails?.customer_email || 'No account/customer snapshot'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[260px]">
                      <div className="flex items-center">
                        <TruckIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-on-surface">
                            {getVehicleLabel(reservation)}
                          </div>
                          <div className="max-w-[420px] truncate text-sm text-gray-500">
                            {reservation.tripDetails?.pickup_location && reservation.tripDetails?.dropoff_location
                              ? `${reservation.tripDetails.pickup_location} -> ${reservation.tripDetails.dropoff_location}`
                              : reservation.tripDetails?.customer_company_name || reservation.vehicle?.licensePlate || reservation.vehicleId || 'No linked vehicle'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface">
                      <div>
                        <div>{formatShortDateTime(reservation.startDate)}</div>
                        <div className="text-xs text-on-surface-variant">to {formatShortDateTime(reservation.endDate)}</div>
                      </div>
                    </td>
	                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface">
	                      <div className="font-medium">{getAssignmentLabel(reservation)}</div>
	                      <div className="text-xs text-gray-500">
	                        {(reservation.vehicleAssignments || []).slice(0, 2).map((assignment: any) => (
	                          assignment.vehicle?.fleetNumber || assignment.vehicle?.licensePlate || assignment.vehicle?.model
	                        )).filter(Boolean).join(', ') || getVehicleLabel(reservation)}
	                      </div>
	                    </td>
	                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface">
	                      <div className="font-medium">{getDriverAssignmentLabel(reservation)}</div>
	                      <div className="text-xs text-gray-500">
	                        {(reservation.driverReadiness?.assigned ?? reservation.driverAssignments?.length ?? 0)}/
	                        {reservation.driverReadiness?.required ?? 0} assigned
	                      </div>
	                    </td>
	                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-on-surface">{formatDollars(reservation.totalPrice)}</div>
                      <span className={`mt-1 inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${reservation.paymentStatus === 'deposit_due' ? 'bg-amber-100 text-amber-800' : reservation.paymentStatus === 'deposit_paid' ? 'bg-green-100 text-green-800' : 'bg-surface-container-high text-on-surface-variant'}`}>
                        {getPaymentLabel(reservation)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getLifecycleColor(reservation.lifecycleLabel)}`}>
                        {reservation.lifecycleLabel || formatLabel(reservation.status)}
                      </span>
                      <div className="mt-1 text-xs text-on-surface-variant">{formatLabel(reservation.status)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <InvoiceActionMenu
                        menuId={reservation.id}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        invoiceNumber={getReservationLabel(reservation)}
                        actions={getReservationActions(reservation)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-on-surface mb-2">No reservations found</h3>
            <p className="text-gray-600">No reservations match your current filters.</p>
          </div>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="bg-surface-container-lowest px-4 py-3 flex items-center justify-between border-t border-ghost">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-ghost text-sm font-medium rounded-md text-gray-700 bg-surface-container-lowest hover:bg-surface-container-low"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.totalPages}
                className="relative inline-flex items-center px-4 py-2 border border-ghost text-sm font-medium rounded-md text-gray-700 bg-surface-container-lowest hover:bg-surface-container-low"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(filters.page - 1) * filters.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(filters.page * filters.limit, pagination.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{pagination.total}</span>{' '}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={filters.page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-ghost bg-surface-container-lowest text-sm font-medium text-gray-500 hover:bg-surface-container-low"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={filters.page >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-ghost bg-surface-container-lowest text-sm font-medium text-gray-500 hover:bg-surface-container-low"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </section>

      <ReservationDetailDrawer
        open={Boolean(selectedReservationId)}
        onClose={() => setSelectedReservationId(null)}
        reservationId={selectedReservationId}
        onReservationUpdated={() => queryClient.invalidateQueries('admin-reservations')}
      />

      <ReservationEditorModal
        open={editorOpen}
        reservationId={editorReservationId}
        onClose={() => {
          setEditorOpen(false);
          setEditorReservationId(null);
        }}
        onSaved={(reservation) => {
          if (reservation?.id) {
            setSelectedReservationId(reservation.id);
          }
        }}
      />

      <EntityDrawer
        open={Boolean(statusAction)}
        onClose={() => {
          setStatusAction(null);
          setStatusReason('');
        }}
        title={statusAction?.mode === 'bulk' ? 'Bulk Status Update' : 'Update Reservation Status'}
        subtitle={
          statusAction
            ? `${statusAction.label} ${statusAction.reservationIds.length} ${statusAction.reservationIds.length === 1 ? 'reservation' : 'reservations'}`
            : undefined
        }
        widthClassName="max-w-md"
        footer={(
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setStatusAction(null);
                setStatusReason('');
              }}
              disabled={updateStatusMutation.isLoading || bulkOperationMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={submitStatusAction}
              disabled={!statusAction || updateStatusMutation.isLoading || bulkOperationMutation.isLoading}
            >
              Apply Update
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="rounded-card border border-outline/60 bg-surface-container-low p-4">
            <p className="text-sm font-semibold text-on-surface">
              {statusAction?.label || 'Status update'}
            </p>
            <p className="mt-1 text-sm text-on-surface-variant">
              Add an optional reason for the operations log. This is internal context for the team.
            </p>
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-on-surface-variant">Reason</span>
            <textarea
              className="input-field min-h-[140px]"
              value={statusReason}
              onChange={(event) => setStatusReason(event.target.value)}
              placeholder="Example: Deposit received by check, customer requested cancellation, trip completed..."
            />
          </label>
        </div>
      </EntityDrawer>
    </PageLayout>
  );
}
